/**
 * Generador de PDF para el cupón individual — Fase 7.3
 *
 * Documentos relacionados:
 *   - DECISIONES.md #005 P1 (formato código + mantener boleta imprimible para ventanilla)
 *   - DECISIONES.md #034 (stack reportes: jspdf cliente)
 *
 * Reemplaza el window.print() anterior que imprimía toda la pantalla del
 * detalle (incluyendo botones, alertas y layout de la app). Esto genera un
 * PDF limpio con solo los datos del cupón, apto para imprimir en ventanilla
 * bancaria o adjuntar por mail.
 *
 * Layout: A4 portrait, márgenes 20mm, tipografía Helvetica (built-in jsPDF
 * para no tener que embedear fuentes).
 */

import { jsPDF } from 'jspdf';
import type { Cupon } from '../../types/cupon';
import { formatCurrency, formatDate, formatCuit, formatPeriodo, formatDateTime } from '../format';
import { formatCvu } from '../../types/cvu';
import type { DatosBancarios } from '../../types/datosBancarios';
import { triggerBlobDownload } from './download';

// ── Tipos auxiliares ──────────────────────────────────────────────────────────

/**
 * Subconjunto del CvuEmpresa que necesita el PDF.
 * Lo dejamos opcional: si el cupón no tiene CVU asociado (empresa sin alta
 * en Pronto Pago todavía), simplemente no se imprime la sección.
 */
export interface CvuParaPdf {
  alias: string;
  cvu: string;
  titular_visible?: string | null;
}

// ── Constantes de layout (mm) ─────────────────────────────────────────────────

const MARGIN_X = 20;
const MARGIN_TOP = 20;
const PAGE_WIDTH = 210; // A4
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN_X * 2;

// Colores en RGB (de brand.json — primary.main #1565C0 y text.primary #212121)
const COLOR_PRIMARY: [number, number, number] = [0x15, 0x65, 0xc0];
const COLOR_TEXT: [number, number, number] = [0x21, 0x21, 0x21];
const COLOR_MUTED: [number, number, number] = [0x61, 0x61, 0x61];
const COLOR_DIVIDER: [number, number, number] = [0xbd, 0xbd, 0xbd];

// ── Helpers internos ──────────────────────────────────────────────────────────

function setColor(
  doc: jsPDF,
  rgb: [number, number, number],
  target: 'text' | 'draw' | 'fill' = 'text',
) {
  if (target === 'text') doc.setTextColor(rgb[0], rgb[1], rgb[2]);
  if (target === 'draw') doc.setDrawColor(rgb[0], rgb[1], rgb[2]);
  if (target === 'fill') doc.setFillColor(rgb[0], rgb[1], rgb[2]);
}

function divider(doc: jsPDF, y: number) {
  setColor(doc, COLOR_DIVIDER, 'draw');
  doc.setLineWidth(0.2);
  doc.line(MARGIN_X, y, PAGE_WIDTH - MARGIN_X, y);
}

/**
 * Devuelve el nombre del archivo PDF para un cupón.
 * Usa el código del cupón ya formateado (ATSA-YYYYMM-CUIT-NNN).
 * Exportado para que el caller pueda mostrar el nombre antes de descargar.
 */
export function getCuponPdfFilename(cupon: Pick<Cupon, 'codigo'>): string {
  return `cupon-${cupon.codigo}.pdf`;
}

// ── Render principal ──────────────────────────────────────────────────────────

/**
 * Construye el documento jsPDF en memoria. No dispara descarga.
 * Útil para tests y para integraciones futuras (preview embebido, attach a email).
 */
export function buildCuponPdf(
  cupon: Cupon,
  cvu?: CvuParaPdf | null,
  banco?: DatosBancarios | null,
): jsPDF {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });

  let y = MARGIN_TOP;

  // ── Header: marca ATSA ──────────────────────────────────────────────────
  setColor(doc, COLOR_PRIMARY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('ATSA Río Negro', MARGIN_X, y);

  setColor(doc, COLOR_MUTED);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(
    'Asociación de Trabajadores de la Sanidad Argentina',
    MARGIN_X,
    y + 5,
  );

  setColor(doc, COLOR_PRIMARY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(
    'BOLETA DE APORTE SINDICAL',
    PAGE_WIDTH - MARGIN_X,
    y,
    { align: 'right' },
  );
  setColor(doc, COLOR_MUTED);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(
    `Reimpresión #${cupon.reimpresion}`,
    PAGE_WIDTH - MARGIN_X,
    y + 5,
    { align: 'right' },
  );

  y += 12;
  divider(doc, y);
  y += 8;

  // ── Código del cupón ────────────────────────────────────────────────────
  setColor(doc, COLOR_MUTED);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('CÓDIGO DE CUPÓN', MARGIN_X, y);
  y += 6;

  setColor(doc, COLOR_TEXT);
  doc.setFont('courier', 'bold');
  doc.setFontSize(16);
  doc.text(cupon.codigo, MARGIN_X, y);
  y += 10;

  // ── Datos de la empresa ────────────────────────────────────────────────
  setColor(doc, COLOR_MUTED);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('EMPRESA', MARGIN_X, y);
  doc.text('CUIT', PAGE_WIDTH - MARGIN_X, y, { align: 'right' });
  y += 5;

  setColor(doc, COLOR_TEXT);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(cupon.empresa?.razon_social ?? '—', MARGIN_X, y);
  doc.setFont('courier', 'normal');
  doc.text(
    formatCuit(cupon.empresa?.cuit ?? null),
    PAGE_WIDTH - MARGIN_X,
    y,
    { align: 'right' },
  );
  y += 9;

  // ── Período + fechas ──────────────────────────────────────────────────
  const colWidth = CONTENT_WIDTH / 3;

  setColor(doc, COLOR_MUTED);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('PERÍODO', MARGIN_X, y);
  doc.text('FECHA DE EMISIÓN', MARGIN_X + colWidth, y);
  doc.text('FECHA DE VENCIMIENTO', MARGIN_X + colWidth * 2, y);
  y += 5;

  setColor(doc, COLOR_TEXT);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  const periodoLabel = cupon.periodo?.periodo
    ? formatPeriodo(cupon.periodo.periodo)
    : '—';
  doc.text(periodoLabel, MARGIN_X, y);
  doc.text(formatDate(cupon.fecha_emision), MARGIN_X + colWidth, y);
  doc.text(
    formatDate(cupon.fecha_vencimiento),
    MARGIN_X + colWidth * 2,
    y,
  );
  y += 10;

  divider(doc, y);
  y += 8;

  // ── Detalle de montos ────────────────────────────────────────────────
  setColor(doc, COLOR_TEXT);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('DETALLE', MARGIN_X, y);
  y += 7;

  // Filas: label izquierda, monto derecha
  const drawMontoRow = (label: string, valor: number, bold = false) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(bold ? 12 : 11);
    setColor(doc, bold ? COLOR_TEXT : COLOR_MUTED);
    doc.text(label, MARGIN_X, y);
    setColor(doc, COLOR_TEXT);
    doc.text(formatCurrency(valor), PAGE_WIDTH - MARGIN_X, y, {
      align: 'right',
    });
    y += 7;
  };

  drawMontoRow('Monto de aportes', cupon.monto_aporte);
  drawMontoRow('Intereses por mora', cupon.interes);

  divider(doc, y - 2);
  y += 1;

  drawMontoRow('TOTAL A PAGAR', cupon.monto_total, true);
  y += 4;

  // ── Datos CVU (si la empresa tiene CVU asignado) ─────────────────────
  if (cvu) {
    divider(doc, y);
    y += 8;

    setColor(doc, COLOR_PRIMARY);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('PAGO POR TRANSFERENCIA (CVU)', MARGIN_X, y);
    y += 7;

    // Tabla 2 columnas: label / valor
    const drawCvuRow = (label: string, valor: string, mono = false) => {
      setColor(doc, COLOR_MUTED);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(label, MARGIN_X, y);

      setColor(doc, COLOR_TEXT);
      doc.setFont(mono ? 'courier' : 'helvetica', 'bold');
      doc.setFontSize(11);
      doc.text(valor, MARGIN_X + 32, y);
      y += 6;
    };

    drawCvuRow('Alias:', cvu.alias, true);
    drawCvuRow('CVU:', formatCvu(cvu.cvu), true);
    drawCvuRow('Titular:', cvu.titular_visible ?? 'ATSA Río Negro');

    y += 2;
    setColor(doc, COLOR_MUTED);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    const noteLines = doc.splitTextToSize(
      'Si su billetera muestra "ATSARN" en el campo destinatario, es la abreviación de ATSA RIO NEGRO. Es el destinatario correcto.',
      CONTENT_WIDTH,
    );
    doc.text(noteLines, MARGIN_X, y);
    y += noteLines.length * 4 + 2;
  }

  // ── Datos bancarios BNA (cuenta única, ADR #042) ─────────────────────
  if (banco) {
    divider(doc, y);
    y += 8;

    setColor(doc, COLOR_PRIMARY);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('PAGO POR TRANSFERENCIA BANCARIA', MARGIN_X, y);
    y += 7;

    const drawBancoRow = (label: string, valor: string, mono = false) => {
      if (!valor) return;
      setColor(doc, COLOR_MUTED);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(label, MARGIN_X, y);

      setColor(doc, COLOR_TEXT);
      doc.setFont(mono ? 'courier' : 'helvetica', 'bold');
      doc.setFontSize(11);
      doc.text(valor, MARGIN_X + 32, y);
      y += 6;
    };

    drawBancoRow('Banco:', banco.banco);
    drawBancoRow('Titular:', banco.razonSocial);
    drawBancoRow('CUIT:', banco.cuit, true);
    drawBancoRow('Cuenta:', banco.cuenta, true);
    drawBancoRow('CBU:', banco.cbu, true);
    drawBancoRow('Alias:', banco.alias, true);

    y += 2;
    setColor(doc, COLOR_MUTED);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    const notaBanco = doc.splitTextToSize(
      `Al transferir, indique en el concepto o referencia el código de cupón ${cupon.codigo} (o el CUIT de su empresa) para identificar su pago. Luego ingrese al sistema, abra este cupón y suba el comprobante de la transferencia (obligatorio para que ATSA confirme el pago).`,
      CONTENT_WIDTH,
    );
    doc.text(notaBanco, MARGIN_X, y);
    y += notaBanco.length * 4 + 2;
  }

  // ── Instrucciones de pago por ventanilla ─────────────────────────────
  divider(doc, y);
  y += 6;

  setColor(doc, COLOR_MUTED);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  const destinoPago =
    cvu && banco ? 'los datos indicados (CVU o cuenta bancaria)'
      : banco ? 'la cuenta bancaria indicada'
      : 'el CVU indicado';
  const instrucciones = doc.splitTextToSize(
    `Esta boleta puede abonarse por transferencia a ${destinoPago} o presentándola por ventanilla en la entidad bancaria. Los intereses por mora se calculan diariamente sobre el monto de aportes a partir de la fecha de vencimiento.`,
    CONTENT_WIDTH,
  );
  doc.text(instrucciones, MARGIN_X, y);
  y += instrucciones.length * 4;

  // ── Footer ───────────────────────────────────────────────────────────
  const footerY = 287;
  divider(doc, footerY - 4);
  setColor(doc, COLOR_MUTED);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(
    `Generado el ${formatDateTime(new Date())}`,
    MARGIN_X,
    footerY,
  );
  doc.text(
    'atsarionegro.com.ar',
    PAGE_WIDTH - MARGIN_X,
    footerY,
    { align: 'right' },
  );

  return doc;
}

/**
 * Genera el PDF y dispara la descarga en el navegador.
 * Es la API de uso normal desde la UI.
 *
 * Devuelve el Blob por si el caller quiere hacer algo más (preview, attach).
 */
export function generarCuponPdf(
  cupon: Cupon,
  cvu?: CvuParaPdf | null,
  banco?: DatosBancarios | null,
): Blob {
  const doc = buildCuponPdf(cupon, cvu, banco);
  const blob = doc.output('blob');
  triggerBlobDownload(blob, getCuponPdfFilename(cupon));
  return blob;
}
